import {isNoneAdvisoryCurrency} from '@stubhub/currency-utils';
import {DELIVERY_METHOD_IDS} from '@stubhub/delivery-utils';

import {TICKET_MEDIUMS} from '../../../../utils/constants';

export const PAYMENT_METHODS = {
  BTPAYPAL: 'btpaypal',
  BTAPPLEPAY: 'btapplepay',
  BTGOOGLEPAY: 'btandroidpay',
};

/**
 * Method that check if apple pay method is enabled to filter the array of payment methods
 * passed in parameters.
 *
 * @param {Array} paymentMethods array with original list of API payment methods
 * @param {boolean} showApplePay boolean value from FF checkout.payment.enableApplePay
 * @returns filtered list of payment methods depending of value of showApplePay value
 */
export const getFilteredPaymentMethods = (paymentMethods, showApplePay) => {
  if (!paymentMethods || paymentMethods.length === 0) {
    return [];
  }

  return paymentMethods.filter(
    (method) =>
      (method.receivablePaymentMethod !== PAYMENT_METHODS.BTAPPLEPAY || showApplePay) &&
      method.receivablePaymentMethod !== PAYMENT_METHODS.BTGOOGLEPAY
  );
};

const PAYLATER_ELIGIBLE_CURRENCIES = ['USD', 'EUR', 'GBP'];

/**
 * Checks if the given currency is eligible for paypal pay later
 * @param {string} currency the currency
 * @returns true if the currency is eligible for pay later, false otherwise
 */
export const isCurrencyEligibleForPayLater = (currency) => PAYLATER_ELIGIBLE_CURRENCIES.includes(currency);

const DIGITAL_TICKET_MEDIUM = [
  TICKET_MEDIUMS.PDF,
  TICKET_MEDIUMS.BARCODE,
  TICKET_MEDIUMS.EXT_MOBILE,
  TICKET_MEDIUMS.MOBILE,
];

/**
 * Returns if the ticket medium is considered digital or not
 * @param {number | string} ticketMedium the ticket medium id
 * @returns true if the ticket is considered digital, false otherwise
 */
export const isDigitalTicket = (ticketMedium) =>
  DIGITAL_TICKET_MEDIUM.some((medium) => medium?.toString() === ticketMedium?.toString());

/**
 * Returns if advisory should be applied. It checks if the selected advisory currency is NONE or if it's the same than the event currency
 * @param {string} selectedCurrency the advisory selected currency
 * @param {string} eventCurrency event currency
 * @returns if advisory price should be applied
 */
export const isAdvisoryApplied = (selectedCurrency, eventCurrency) =>
  !!selectedCurrency && !isNoneAdvisoryCurrency(selectedCurrency) && selectedCurrency !== eventCurrency;

/**
 * Returns true if the price is shown with fees, the ticket is digital and advisory is not applied
 * @param {boolean} priceWithFees if the price is shown with fees
 * @param {number} ticketMedium the ticket medium
 * @param {string} advisoryCurrency the selected advisory currency
 * @param {string} eventCurrency the event currency
 * @returns true or false
 */
export const isPayLaterAmountVisible = (priceWithFees, ticketMedium, advisoryCurrency, eventCurrency) =>
  priceWithFees && isDigitalTicket(ticketMedium) && !isAdvisoryApplied(advisoryCurrency, eventCurrency);

/**
 * Returns the rounded price if the amount is enabled and it's a valid price. Otherwise, it returns undefined
 * @param {boolean} isEnabled if pay later amount is enabled
 * @param {number | string} price the ticket price
 * @returns the rounded amount or undefined if not valid or enabled
 */
export const getPayLaterAmount = (isEnabled, price) =>
  isEnabled && price !== '' && price !== null && !isNaN(price) ? Math.round(price) : undefined;

/**
 * Checks if the listing will require a local delivery address
 * @param {number} ticketMedium the ticket medium id
 * @param {Array} deliveryMethods the delivery method array for a listing
 * @returns if the delivery  will require local address
 */
export const needsLocalDeliveryAddress = (ticketMedium, deliveryMethods) =>
  (ticketMedium === TICKET_MEDIUMS.PAPER || ticketMedium === TICKET_MEDIUMS.SEASON_CARD) &&
  !!deliveryMethods?.some(
    (deliveryMethod) =>
      deliveryMethod?.id === DELIVERY_METHOD_IDS.UPS_STANDARD_INTRA ||
      deliveryMethod?.id === DELIVERY_METHOD_IDS.COURIER
  );
